import { useEffect, useMemo } from "react";
import usePaymentMethodAvailability from "api/PaymentsResource/usePaymentMethodAvailability";
import {
	buildDefaultAvailabilityMap,
	getPaymentOptionsForCurrency,
} from "utils/paymentOptions";

const usePaymentMethodOptions = (currency, paymentMethod, setPaymentMethod) => {
	const { data: availabilityMap, isLoading, isError } = usePaymentMethodAvailability();

	const resolvedAvailabilityMap = useMemo(() => {
		if (isError || !availabilityMap) {
			return Object.fromEntries(Object.entries(buildDefaultAvailabilityMap()).map(([key, config]) => [key, { ...config, enabled: false }]));
		}

		return availabilityMap;
	}, [availabilityMap, isError]);

	const { allOptions, enabledOptions, disabledOptions } = useMemo(
		() => getPaymentOptionsForCurrency(currency, resolvedAvailabilityMap),
		[currency, resolvedAvailabilityMap],
	);

	const selectedOption = useMemo(
		() => allOptions.find((option) => option.paymentMethod === paymentMethod) || null,
		[allOptions, paymentMethod],
	);

	const hasEnabledOptions = enabledOptions.length > 0;

	useEffect(() => {
		if (!setPaymentMethod) {
			return;
		}

		const currentOption = allOptions.find((option) => option.paymentMethod === paymentMethod);

		if (currentOption?.enabled) {
			return;
		}

		if (enabledOptions.length > 0) {
			setPaymentMethod(enabledOptions[0].paymentMethod);
			return;
		}

		if (paymentMethod) {
			setPaymentMethod(null);
		}
	}, [allOptions, enabledOptions, paymentMethod, setPaymentMethod]);

	return {
		options: allOptions,
		enabledOptions,
		disabledOptions,
		selectedOption,
		hasEnabledOptions,
		isLoading,
	};
};

export default usePaymentMethodOptions;
